/*
 * Copyright (c) CIRROM 2021.
 */

import React, { useCallback, useEffect, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import {
  CRMDatabaseIDCellRenderer,
  GoogleSearchRenderer,
  HumanReadableFormatRenderer,
  LinkedInSearchRenderer,
  ProbabilityCellRenderer,
  ValueChangeRenderer,
} from "./CellRenderers";
import { CirromGridOverlay } from "./CustomOverlay";
import { SuggestedChangeDetailsModal } from "./RecordDetails";

import { GridStateToolbar } from "./GridStateToolbar";
import { IRowNode } from "ag-grid-community";

/**
 * Common constructor for Suggested changes grid
 * Used in all the user pages showing a grid with Cirrom changes
 *  */
export const SuggestedChangesGrid = React.memo(
  function SuggestedChangesGrid(props: {
    onGridReady?: (params) => void;
    onFirstDataRendered?: (params) => void;
    onRowDataChanged?: (params) => void;
    onSelectionChanged?: (params) => void;
    suggestedChanges: { columns: any[]; rows: any[] };
    gridFilterModel: any;
    defaultProbabilitySlider?: number[];
    chartFilter?: string;
    gridName?: string;
    selectedRowCounts?: number;
  }) {
    const { gridFilterModel } = props;
    const [gridApi, setGridApi] = useState(null);
    const [isDetailsOpen, setIsDetailsOpen] = useState<boolean>(false);
    const [quickFilterValue, setQuickFilterValue] = useState<string>("");
    const [rowCount, setRowCount] = useState<number>(0);
    const [updatedFilterModel, setUpdatedFilterModel] = useState<any>({});
    const [selectedDetailsNode, setSelectedDetailsNode] =
      useState<IRowNode>(undefined);
    const [nextDetailsNode, setNextDetailsNode] = useState<IRowNode>(undefined);
    const [previousDetailsNode, setPreviousDetailsNode] =
      useState<IRowNode>(undefined);
    const [rowDataChanged, setRowDataChanged] = useState<boolean>(false);

    const [humanSortOrder, setHumanSortOrder] = useState<any>({});

    /**
     * When the grid is initialized, keep track of all api vars
     */
    const onGridReady = (params) => {
      if (props.onGridReady) {
        props.onGridReady(params);
      }
      setGridApi(params.api);
    };
    useEffect(() => {
      setQuickFilterValue(props.chartFilter);
    }, [props.chartFilter]);

    /**
     * Reset the quick filters, deselect all but keep track of previous grid filter models
     * Used when switching tabs on the same page
     */
    function clearPendingInteractions() {
      if (
        gridFilterModel &&
        gridApi?.getColumn(Object.keys(gridFilterModel)[0])
      ) {
        gridApi.setFilterModel(null);
        setQuickFilterValue("");
        gridApi.setGridOption("quickFilterText", null);
        gridApi.deselectAll();
        gridApi.setFilterModel(gridFilterModel || {});
        setTimeout(function () {
          gridApi.autoSizeAllColumns();
        }, 1);
      }
    }

    /**
     * Refresh the grid state
     */
    useEffect(() => {
      clearPendingInteractions();
    }, [gridApi, gridFilterModel]);

    /**
     * When new data is added to the grid
     * Update row counts and restore column state if available
     * Note: clearPendingInteractions() is intentionally NOT called here to preserve
     * user-applied filters and column widths during bulk operations (approve/reject).
     * It's only called when gridFilterModel changes (tab switches) via the useEffect below.
     * @param event
     */
    const onRowDataUpdated = (event) => {
      if (props.onRowDataChanged) {
        props.onRowDataChanged(event);
      }
      setRowCount(event.api.getDisplayedRowCount());
      setRowDataChanged(!rowDataChanged);
      if (gridApi) {
        // Restore saved column state if available
        if (
          window.localStorage.getItem(
            `suggested-changes-grid-${props.gridName}`
          )
        ) {
          const gridState = JSON.parse(
            window.localStorage.getItem(
              `suggested-changes-grid-${props.gridName}`
            )
          );
          const filteredGridState = gridState.filter((item) => item.pinned);
          if (filteredGridState.length === 0) {
            gridState[0].pinned = "left";
          }

          gridApi.applyColumnState({
            state: gridState,
            applyOrder: true,
          });
        } else {
          // Only auto-size columns if there's no saved state (first load or no localStorage)
          gridApi.autoSizeAllColumns();
        }
      }
    };

    /**
     * On quick filter changes, apply the to the grid
     */
    useEffect(() => {
      if (gridApi) {
        gridApi.setGridOption("quickFilterText", quickFilterValue);
      }
    }, [quickFilterValue, gridApi]);

    const setQuickFilterCallback = useCallback((quickFilter) => {
      setQuickFilterValue(quickFilter);
    }, []);

    /**
     * Keep track of what the previous and next nodes are in the grid
     */
    useEffect(() => {
      if (gridApi && selectedDetailsNode) {
        setNextDetailsNode(
          gridApi.getDisplayedRowAtIndex(selectedDetailsNode.rowIndex + 1)
        );
        setPreviousDetailsNode(
          selectedDetailsNode.rowIndex > 0
            ? gridApi.getDisplayedRowAtIndex(selectedDetailsNode.rowIndex - 1)
            : undefined
        );
      }
    }, [selectedDetailsNode, gridApi]);

    /**
     * When the previous row is selected, mark the corresponding checkbox on the grid
     */
    const onPreviousRowSelected = () => {
      gridApi.deselectAll();
      previousDetailsNode.setSelected(true);
      setSelectedDetailsNode(previousDetailsNode);
    };

    /**
     * When the next row is selected, mark the corresponding checkbox on the grid
     */
    const onNextRowSelected = () => {
      gridApi.deselectAll();
      nextDetailsNode.setSelected(true);
      setSelectedDetailsNode(nextDetailsNode);
    };

    /**
     * When there's a manual change in column state, persist it to local storage
     */
    function persistNewColumnState(event) {
      if (event.columns.length === 1) {
        window.localStorage.setItem(
          `suggested-changes-grid-${props.gridName}`,
          JSON.stringify(event.api.getColumnState())
        );
      }
    }

    return (
      <>
        <GridStateToolbar
          setQuickFilterValue={setQuickFilterCallback}
          quickFilterValue={quickFilterValue}
          gridApi={gridApi}
          showExportButton={true}
          rowCount={rowCount}
          gridFilterModel={updatedFilterModel}
          defaultProbability={props.defaultProbabilitySlider}
          rowDataChanged={rowDataChanged}
        />
        <div style={{ height: "calc(100%)" }}>
          <div style={{ height: "calc(100% - 30px)" }}>
            <AgGridReact
              onGridReady={onGridReady}
              onRowDataUpdated={onRowDataUpdated}
              onFilterChanged={(event) => {
                setRowCount(event.api.getDisplayedRowCount());
              }}
              onFirstDataRendered={(params) => {
                if (props.onFirstDataRendered) {
                  props.onFirstDataRendered(params);
                }
                if (
                  window.localStorage.getItem(
                    `suggested-changes-grid-${props.gridName}`
                  )
                ) {
                  const gridState = JSON.parse(
                    window.localStorage.getItem(
                      `suggested-changes-grid-${props.gridName}`
                    )
                  );

                  const filteredGridState = gridState.filter(
                    (item) => item.pinned
                  );
                  if (filteredGridState.length === 0) {
                    gridState[0].pinned = "left";
                  }
                  params.api.applyColumnState({
                    state: gridState,
                    applyOrder: true,
                  });
                }
              }}
              onFilterModified={(ev) => {
                setUpdatedFilterModel(ev.api.getFilterModel());
              }}
              columnDefs={props.suggestedChanges.columns}
              rowData={props.suggestedChanges.rows}
              suppressModelUpdateAfterUpdateTransaction={true}
              rowBuffer={50}
              components={{
                probabilityCellRenderer: ProbabilityCellRenderer,
                valueChangeRenderer: ValueChangeRenderer,
                humanReadableFormatRenderer: HumanReadableFormatRenderer,
                cirromGridOverlay: CirromGridOverlay,
                googleSearchRenderer: GoogleSearchRenderer,
                linkedinSearchRenderer: LinkedInSearchRenderer,
                crmDatabaseIdRenderer: CRMDatabaseIDCellRenderer,
              }}
              rowSelection={{
                mode: "multiRow",
                enableClickSelection: !(
                  props.selectedRowCounts && props.selectedRowCounts > 1
                ),
                checkboxes: true,
                headerCheckbox: true,
                selectAll: "filtered",
              }}
              selectionColumnDef={{
                pinned: "left",
                lockPosition: "left",
                suppressMovable: true,
                width: 50,
                maxWidth: 50,
                resizable: false,
              }}
              onSelectionChanged={props.onSelectionChanged}
              onSortChanged={(event) => {
                if (event.source === "uiColumnSorted") {
                  setHumanSortOrder(
                    event.api.getColumnState().filter((s) => s.sort !== null)
                  );
                } else if (event.source === "api" && humanSortOrder) {
                  event.api.applyColumnState({
                    state: humanSortOrder.map((hso) => {
                      return { colId: hso.colId, sort: hso.sort };
                    }),
                    defaultState: { sort: null },
                  });
                }
              }}
              onRowDoubleClicked={(event) => {
                setSelectedDetailsNode(event.node);
                setIsDetailsOpen(true);
              }}
              loadingOverlayComponent="cirromGridOverlay"
              getRowId={(params) => String(params.data.id)}
              onColumnResized={(event) => {
                persistNewColumnState(event);
              }}
              onColumnMoved={(event) => {
                persistNewColumnState(event);
              }}
              onColumnVisible={(event) => {
                persistNewColumnState(event);
              }}
              includeHiddenColumnsInQuickFilter={true}
              includeHiddenColumnsInAdvancedFilter={true}
            />
          </div>
        </div>

        <SuggestedChangeDetailsModal
          open={isDetailsOpen}
          onClose={() => setIsDetailsOpen(false)}
          onPreviousRowRequested={
            previousDetailsNode ? onPreviousRowSelected : undefined
          }
          onNextRowRequested={nextDetailsNode ? onNextRowSelected : undefined}
          suggestedChangeId={selectedDetailsNode?.data.id}
          batchId={selectedDetailsNode?.data.batch_id}
          onSuccess={(suggestedId?) => {
            if (suggestedId) {
              gridApi.applyTransaction({
                remove: [gridApi.getRowNode(suggestedId)],
              });
            }
          }}
        />
      </>
    );
  }
);
